import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { GlassCard } from "@/components/common/GlassCard";
import { PageHeader } from "@/components/common/PageHeader";
import { LoadingState } from "@/components/common/States";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PositionArchetypeFields } from "@/components/common/PositionArchetypeFields";
import { Plus, Trash2, UserPlus, Users as UsersIcon, Shield, X, Check, Copy, Phone, KeyRound, Inbox, RefreshCw } from "lucide-react";
import { StatusPill } from "@/components/common/StatusPill";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

export default function AdminPanelPage() {
  const [tab, setTab] = useState("players");
  const [pending, setPending] = useState(0);

  const refreshPending = () =>
    api.get("/auth/reset-requests")
      .then((r) => setPending(r.data.filter((x) => x.status === "pending").length))
      .catch(() => {});
  useEffect(() => { refreshPending(); }, []);

  return (
    <div className="max-w-[1500px] mx-auto">
      <PageHeader
        title="Admin Panel"
        subtitle="Manage players, users, and assignments."
      />
      <Tabs value={tab} onValueChange={setTab} className="">
        <TabsList className="glass border border-white/10 bg-white/[0.04]">
          <TabsTrigger data-testid="admin-tab-players" value="players" className="data-[state=active]:bg-cyan-300/15 data-[state=active]:text-cyan-200">
            <UsersIcon className="h-3.5 w-3.5" /> Players
          </TabsTrigger>
          <TabsTrigger data-testid="admin-tab-users" value="users" className="data-[state=active]:bg-cyan-300/15 data-[state=active]:text-cyan-200">
            <Shield className="h-3.5 w-3.5" /> Users
          </TabsTrigger>
          <TabsTrigger data-testid="admin-tab-reset-requests" value="reset-requests" className="data-[state=active]:bg-cyan-300/15 data-[state=active]:text-cyan-200">
            <Inbox className="h-3.5 w-3.5" /> Reset Requests
            {pending > 0 && (
              <span data-testid="admin-reset-badge" className="ml-1.5 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-rose-500 text-white text-[10px] font-semibold">{pending}</span>
            )}
          </TabsTrigger>
        </TabsList>
        <TabsContent value="players" className="mt-5"><PlayersTab /></TabsContent>
        <TabsContent value="users" className="mt-5"><UsersTab onPasswordReset={refreshPending} /></TabsContent>
        <TabsContent value="reset-requests" className="mt-5"><ResetRequestsTab onChange={refreshPending} /></TabsContent>
      </Tabs>
    </div>
  );
}

function PlayersTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const nav = useNavigate();

  const load = () => api.get("/players").then((r) => setItems(r.data)).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    if (!confirm("Delete this player and all related data?")) return;
    await api.delete(`/players/${id}`);
    setItems((arr) => arr.filter((p) => p.id !== id));
    toast.success("Player deleted");
  };

  if (loading) return <LoadingState />;
  return (
    <div>
      <div className="flex justify-end mb-3"><Button data-testid="admin-new-player" onClick={() => setOpen(true)} className="cta-primary"><Plus className="h-4 w-4" /> New player</Button></div>
      <GlassCard className="p-0 overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-3 text-[11px] uppercase tracking-wider bg-white/[0.03]">
              <th className="text-left p-3">Name</th>
              <th className="text-left p-3">Position</th>
              <th className="text-left p-3">School</th>
              <th className="text-left p-3">Round</th>
              <th className="text-right p-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.map((p) => (
              <tr key={p.id} className="border-t border-white/5 hover:bg-white/[0.04]" data-testid={`admin-player-row-${p.id}`}>
                <td className="p-3 text-1 font-medium">{p.full_name}</td>
                <td className="p-3"><StatusPill tone="cyan">{p.position}</StatusPill></td>
                <td className="p-3 text-2">{p.school || "—"}</td>
                <td className="p-3 text-2">{p.projected_round || "—"}</td>
                <td className="p-3 text-right">
                  <Button onClick={() => nav(`/app/players/${p.id}/profile`)} variant="ghost" size="sm" className="text-2 hover:text-white">Open</Button>
                  <Button onClick={() => remove(p.id)} variant="ghost" size="sm" className="text-rose-300"><Trash2 className="h-4 w-4" /></Button>
                </td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan="5" className="p-8 text-center text-2">No players.</td></tr>}
          </tbody>
        </table>
      </GlassCard>
      {open && <NewPlayerDialog onClose={() => setOpen(false)} onCreated={(p) => setItems((arr) => [p, ...arr])} />}
    </div>
  );
}

function NewPlayerDialog({ onClose, onCreated }) {
  const [form, setForm] = useState({
    full_name: "", position: "QB", archetype: "", school: "", class_year: "Senior",
    jersey_number: "", hometown: "", projected_round: "Round 3", projected_pick_range: "", confidence: 60, bio: "", narrative_md: "",
  });
  const submit = async () => {
    if (!form.full_name) return toast.error("Name required");
    try {
      const r = await api.post("/players", form);
      onCreated(r.data);
      toast.success("Player created");
      onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl bg-[color:var(--canvas-2)]/95 backdrop-blur-2xl border-white/10" data-testid="admin-new-player-dialog">
        <DialogHeader><DialogTitle className="text-1">Create player</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2">
            <PositionArchetypeFields
              position={form.position}
              archetype={form.archetype}
              onPositionChange={(v) => setForm((f) => ({ ...f, position: v }))}
              onArchetypeChange={(v) => setForm((f) => ({ ...f, archetype: v }))}
              testIdPrefix="admin-new-player"
            />
          </div>
          {[["full_name", "Full name"], ["school", "School"], ["class_year", "Class"], ["jersey_number", "Jersey #"], ["hometown", "Hometown"], ["projected_round", "Projected round"], ["projected_pick_range", "Pick range"]].map(([k, label]) => (
            <div key={k} className="space-y-1">
              <Label className="text-2 text-xs">{label}</Label>
              <Input data-testid={`admin-new-player-${k}`} value={form[k] || ""} onChange={(e) => setForm((f) => ({ ...f, [k]: e.target.value }))} className="bg-white/5 border-white/10 text-1" />
            </div>
          ))}
          <div className="col-span-2 space-y-1">
            <Label className="text-2 text-xs">Confidence ({form.confidence}%)</Label>
            <Input type="range" min="0" max="100" value={form.confidence} onChange={(e) => setForm((f) => ({ ...f, confidence: parseInt(e.target.value) }))} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} className="text-2">Cancel</Button>
          <Button data-testid="admin-new-player-save" onClick={submit} className="cta-primary">Create</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function UsersTab({ onPasswordReset }) {
  const [items, setItems] = useState([]);
  const [players, setPlayers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [resetUser, setResetUser] = useState(null);

  const load = async () => {
    const [a, b] = await Promise.all([api.get("/users"), api.get("/players")]);
    setItems(a.data); setPlayers(b.data); setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const toggleDisabled = async (u) => {
    const r = await api.patch(`/users/${u.id}`, { disabled: !u.disabled });
    setItems((arr) => arr.map((x) => x.id === u.id ? r.data : x));
  };
  const remove = async (u) => {
    if (!confirm("Delete user?")) return;
    await api.delete(`/users/${u.id}`);
    setItems((arr) => arr.filter((x) => x.id !== u.id));
  };
  if (loading) return <LoadingState />;
  return (
    <div>
      <div className="flex justify-end mb-3"><Button data-testid="admin-new-user" onClick={() => setOpen(true)} className="cta-primary"><UserPlus className="h-4 w-4" /> New user</Button></div>
      <GlassCard className="p-0 overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-3 text-[11px] uppercase tracking-wider bg-white/[0.03]">
              <th className="text-left p-3">Name</th>
              <th className="text-left p-3">Email</th>
              <th className="text-left p-3">Role</th>
              <th className="text-left p-3">Linked Player</th>
              <th className="text-left p-3">Status</th>
              <th className="text-right p-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.map((u) => (
              <tr key={u.id} className="border-t border-white/5 hover:bg-white/[0.04]" data-testid={`admin-user-row-${u.id}`}>
                <td className="p-3 text-1 font-medium">{u.full_name}</td>
                <td className="p-3 text-2">{u.email}</td>
                <td className="p-3"><StatusPill tone={u.role === "admin" ? "cyan" : u.role === "player" ? "emerald" : "neutral"}>{u.role}</StatusPill></td>
                <td className="p-3 text-2">{players.find((p) => p.id === u.player_id)?.full_name || "—"}</td>
                <td className="p-3">{u.disabled ? <StatusPill tone="rose">Disabled</StatusPill> : <StatusPill tone="emerald">Active</StatusPill>}</td>
                <td className="p-3 text-right whitespace-nowrap">
                  <Button onClick={() => setResetUser(u)} variant="ghost" size="sm" className="text-cyan-300 hover:text-cyan-200" data-testid={`admin-reset-pw-${u.id}`}><KeyRound className="h-4 w-4" /> Reset password</Button>
                  <Button onClick={() => toggleDisabled(u)} variant="ghost" size="sm" className="text-2 hover:text-white">{u.disabled ? "Enable" : "Disable"}</Button>
                  <Button onClick={() => remove(u)} variant="ghost" size="sm" className="text-rose-300"><Trash2 className="h-4 w-4" /></Button>
                </td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan="6" className="p-8 text-center text-2">No users.</td></tr>}
          </tbody>
        </table>
      </GlassCard>
      {open && <NewUserDialog players={players} onClose={() => setOpen(false)} onCreated={(u) => setItems((arr) => [...arr, u])} />}
      {resetUser && (
        <ResetPasswordDialog
          user={resetUser}
          onClose={() => setResetUser(null)}
          onDone={() => { onPasswordReset?.(); }}
        />
      )}
    </div>
  );
}

function NewUserDialog({ players, onClose, onCreated }) {
  const [form, setForm] = useState({
    email: "",
    full_name: "",
    role: "player",
    password: "",
    phone: "",
    player_ids: [], // multi-select
  });
  const [createdUser, setCreatedUser] = useState(null);
  const [busy, setBusy] = useState(false);

  const togglePlayer = (id) => {
    setForm((f) => {
      const has = f.player_ids.includes(id);
      return { ...f, player_ids: has ? f.player_ids.filter((x) => x !== id) : [...f.player_ids, id] };
    });
  };

  const submit = async () => {
    if (!form.email || !form.password || !form.full_name) {
      return toast.error("Full name, email & password required");
    }
    const pwIssues = passwordIssues(form.password);
    if (pwIssues.length) {
      return toast.error(`Password must include ${pwIssues.join(", ")}.`);
    }
    setBusy(true);
    try {
      const payload = {
        email: form.email.trim(),
        full_name: form.full_name.trim(),
        role: form.role,
        password: form.password,
        phone: form.phone.trim() || undefined,
        player_ids: form.player_ids,
      };
      const r = await api.post("/users", payload);
      onCreated(r.data);
      setCreatedUser({ ...r.data, _tempPassword: form.password }); // keep temp pw locally for invite snippet
      toast.success("User created");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally {
      setBusy(false);
    }
  };

  // If we've already created the user, show the invite snippet panel.
  if (createdUser) {
    return (
      <InviteSnippetPanel
        user={createdUser}
        players={players}
        onClose={onClose}
      />
    );
  }

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl bg-[color:var(--canvas-2)]/95 backdrop-blur-2xl border-white/10" data-testid="admin-new-user-dialog">
        <DialogHeader>
          <DialogTitle className="text-1">Create user</DialogTitle>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-3 mt-1">
          <div className="space-y-1">
            <Label className="text-2 text-xs">Full name *</Label>
            <Input
              data-testid="admin-new-user-name"
              value={form.full_name}
              onChange={(e) => setForm((f) => ({ ...f, full_name: e.target.value }))}
              className="bg-white/5 border-white/10 text-1"
            />
          </div>
          <div className="space-y-1">
            <Label className="text-2 text-xs">Email *</Label>
            <Input
              data-testid="admin-new-user-email"
              type="email"
              value={form.email}
              onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
              className="bg-white/5 border-white/10 text-1"
              placeholder="name@example.com"
            />
          </div>
          <div className="space-y-1">
            <Label className="text-2 text-xs">Role *</Label>
            <Select value={form.role} onValueChange={(v) => setForm((f) => ({ ...f, role: v }))}>
              <SelectTrigger className="bg-white/5 border-white/10 text-1" data-testid="admin-new-user-role">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="glass-strong border-white/10">
                <SelectItem value="admin">Admin</SelectItem>
                <SelectItem value="player">Player</SelectItem>
                <SelectItem value="family">Family</SelectItem>
                <SelectItem value="trainer">Trainer</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label className="text-2 text-xs">Phone</Label>
            <Input
              data-testid="admin-new-user-phone"
              type="tel"
              value={form.phone}
              onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
              className="bg-white/5 border-white/10 text-1"
              placeholder="(615) 555-0100"
            />
          </div>

          <div className="col-span-2 space-y-1">
            <Label className="text-2 text-xs">Password *</Label>
            <div className="flex items-center gap-2">
              <Input
                data-testid="admin-new-user-password"
                type="text"
                value={form.password}
                onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
                className="bg-white/5 border-white/10 text-1 font-mono"
                placeholder="Temporary password the user will receive"
              />
              <Button type="button" variant="outline" size="sm" onClick={() => setForm((f) => ({ ...f, password: generatePassword() }))} data-testid="admin-new-user-generate" className="shrink-0 border-white/15 text-2"><RefreshCw className="h-3.5 w-3.5" /> Generate</Button>
            </div>
            {form.password && passwordIssues(form.password).length > 0 ? (
              <div className="text-rose-300 text-[11px]">Must include {passwordIssues(form.password).join(", ")}.</div>
            ) : (
              <div className="text-3 text-[11px] italic">Min 8 chars · upper · lower · number · symbol. The user changes it on first login.</div>
            )}
          </div>

          <div className="col-span-2 space-y-1.5">
            <div className="flex items-center justify-between">
              <Label className="text-2 text-xs">Linked players</Label>
              <span className="text-3 text-[10px]">
                {form.player_ids.length === 0 ? "None — admin-only access" :
                 form.player_ids.length === 1 ? "1 athlete linked" :
                 `${form.player_ids.length} athletes linked`}
              </span>
            </div>
            {form.role === "admin" ? (
              <div className="glass p-3 text-3 text-xs italic">
                Admins have global access — no player linkage needed.
              </div>
            ) : (
              <div
                className="glass p-3 max-h-44 overflow-y-auto space-y-1"
                data-testid="admin-new-user-players-list"
              >
                {players.length === 0 ? (
                  <div className="text-3 text-xs italic">No players in the system yet.</div>
                ) : (
                  players.map((p) => {
                    const selected = form.player_ids.includes(p.id);
                    return (
                      <button
                        key={p.id}
                        type="button"
                        onClick={() => togglePlayer(p.id)}
                        data-testid={`admin-new-user-player-${p.id}`}
                        className={`w-full flex items-center justify-between gap-3 px-2.5 py-1.5 rounded-md text-left transition ${
                          selected ? "bg-cyan-300/15 ring-1 ring-cyan-300/30" : "hover:bg-white/[0.04]"
                        }`}
                      >
                        <span className={`text-sm font-medium ${selected ? "text-cyan-200" : "text-1"}`}>
                          {p.full_name}
                          <span className="text-3 text-[11px] font-normal ml-2">{p.position}{p.school ? ` · ${p.school}` : ""}</span>
                        </span>
                        <span className={`h-4 w-4 rounded grid place-items-center ring-1 transition ${
                          selected ? "bg-cyan-300 text-[#04111A] ring-cyan-300" : "bg-transparent ring-white/15"
                        }`}>
                          {selected && <Check className="h-3 w-3" strokeWidth={3} />}
                        </span>
                      </button>
                    );
                  })
                )}
              </div>
            )}
            {form.role === "family" && form.player_ids.length > 0 && (
              <div className="text-cyan-300/80 text-[11px] italic flex items-center gap-1.5">
                <Check className="h-3 w-3" strokeWidth={3} />
                A private message thread will be auto-created for each linked athlete.
              </div>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={onClose} className="text-2">Cancel</Button>
          <Button
            data-testid="admin-new-user-save"
            onClick={submit}
            disabled={busy}
            className="cta-primary"
          >
            {busy ? "Creating…" : "Create user"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function InviteSnippetPanel({ user, players, onClose }) {
  const linkedPlayers = (user.player_ids || []).map((id) => players.find((p) => p.id === id)?.full_name).filter(Boolean);
  const loginUrl = `${window.location.origin}/login`;

  const snippet =
`Hi ${user.full_name.split(" ")[0]},

Welcome to OmniDraftLab.

Your account is ready. Sign in at:
  ${loginUrl}

Email:    ${user.email}
Password: ${user._tempPassword}
${linkedPlayers.length ? `\nLinked athlete${linkedPlayers.length > 1 ? "s" : ""}: ${linkedPlayers.join(", ")}\n` : ""}
You can change your password after signing in.

— Brandon
OmniDraftLab`;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(snippet);
      toast.success("Invite copied to clipboard");
    } catch {
      toast.error("Copy failed — select & copy manually");
    }
  };

  const mailto = `mailto:${user.email}?subject=${encodeURIComponent("Welcome to OmniDraftLab")}&body=${encodeURIComponent(snippet)}`;

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-xl bg-[color:var(--canvas-2)]/95 backdrop-blur-2xl border-white/10" data-testid="admin-invite-snippet-dialog">
        <DialogHeader>
          <DialogTitle className="text-1 flex items-center gap-2">
            <Check className="h-4 w-4 text-emerald-400" strokeWidth={3} />
            User created — send the invite
          </DialogTitle>
        </DialogHeader>

        <div className="mt-1 text-3 text-sm leading-snug">
          OmniDraftLab does not yet send welcome emails automatically. Copy this and send it to <span className="text-1 font-medium">{user.email}</span>:
        </div>

        <pre
          data-testid="admin-invite-snippet-text"
          className="mt-3 glass p-4 text-2 text-[13px] font-mono leading-relaxed whitespace-pre-wrap break-words rounded-lg max-h-72 overflow-y-auto"
        >
{snippet}
        </pre>

        <DialogFooter className="flex flex-col sm:flex-row sm:items-center gap-2">
          {user.phone && (
            <a
              href={`sms:${user.phone}`}
              className="inline-flex items-center gap-2 px-3 py-2 rounded-lg text-2 hover:text-1 text-sm border border-white/10"
              data-testid="admin-invite-sms"
            >
              <Phone className="h-3.5 w-3.5" /> SMS this user
            </a>
          )}
          <a
            href={mailto}
            className="inline-flex items-center gap-2 px-3 py-2 rounded-lg text-2 hover:text-1 text-sm border border-white/10"
            data-testid="admin-invite-mailto"
          >
            Open in mail app
          </a>
          <div className="flex-1" />
          <Button onClick={copy} className="cta-primary" data-testid="admin-invite-copy">
            <Copy className="h-3.5 w-3.5" /> Copy invite
          </Button>
          <Button variant="ghost" onClick={onClose} className="text-2" data-testid="admin-invite-done">Done</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


// ---------------------------------------------------------------------------
// Password policy helpers (mirror backend security.password_policy_errors)
// ---------------------------------------------------------------------------
function passwordIssues(pw) {
  const out = [];
  if ((pw || "").length < 8) out.push("at least 8 characters");
  if (!/[A-Z]/.test(pw)) out.push("an uppercase letter");
  if (!/[a-z]/.test(pw)) out.push("a lowercase letter");
  if (!/[0-9]/.test(pw)) out.push("a number");
  if (!/[^A-Za-z0-9]/.test(pw)) out.push("a special character");
  return out;
}

function generatePassword() {
  const upper = "ABCDEFGHJKLMNPQRSTUVWXYZ";
  const lower = "abcdefghijkmnpqrstuvwxyz";
  const nums = "23456789";
  const special = "!@#$%&*?";
  const all = upper + lower + nums + special;
  const pick = (s) => s[Math.floor(Math.random() * s.length)];
  let pw = pick(upper) + pick(lower) + pick(nums) + pick(special);
  for (let i = 0; i < 8; i++) pw += pick(all);
  return pw.split("").sort(() => Math.random() - 0.5).join("");
}

function ResetPasswordDialog({ user, onClose, onDone }) {
  const [pw, setPw] = useState("");
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  const issues = passwordIssues(pw);

  const submit = async () => {
    if (issues.length) { toast.error("Password doesn't meet the policy"); return; }
    setBusy(true);
    try {
      await api.patch(`/users/${user.id}`, { password: pw });
      toast.success(`Password reset for ${user.full_name || user.email}. They'll be asked to set their own on next sign-in.`);
      onDone?.(pw);
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to reset password");
    } finally { setBusy(false); }
  };

  const copy = () => { navigator.clipboard.writeText(pw); setCopied(true); setTimeout(() => setCopied(false), 1500); };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent data-testid="admin-reset-password-dialog">
        <DialogHeader>
          <DialogTitle>Reset password — {user.full_name || user.email}</DialogTitle>
        </DialogHeader>
        <div className="py-1 space-y-3">
          <p className="text-2 text-[13px]">Set a temporary password to share with the user. They'll be required to change it on their next sign-in.</p>
          <div>
            <Label className="text-2 text-xs">Temporary password</Label>
            <div className="flex items-center gap-2 mt-1.5">
              <Input data-testid="admin-reset-password-input" value={pw} onChange={(e) => setPw(e.target.value)} placeholder="Enter or generate a password" className="bg-white/5 border-white/10 text-1 font-mono" />
              <Button type="button" variant="outline" size="sm" onClick={() => setPw(generatePassword())} data-testid="admin-reset-generate" className="shrink-0 border-white/15 text-2"><RefreshCw className="h-3.5 w-3.5" /> Generate</Button>
              <Button type="button" variant="outline" size="sm" onClick={copy} disabled={!pw} className="shrink-0 border-white/15 text-2">{copied ? <Check className="h-3.5 w-3.5 text-emerald-400" /> : <Copy className="h-3.5 w-3.5" />}</Button>
            </div>
            {pw && issues.length > 0 && (
              <div className="mt-2 text-[11px] text-rose-300" data-testid="admin-reset-policy-hint">Must include {issues.join(", ")}.</div>
            )}
            {pw && issues.length === 0 && (
              <div className="mt-2 text-[11px] text-emerald-300 inline-flex items-center gap-1"><Check className="h-3 w-3" /> Meets password policy</div>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} className="text-2">Cancel</Button>
          <Button onClick={submit} disabled={busy || issues.length > 0 || !pw} className="cta-primary" data-testid="admin-reset-password-submit">{busy ? "Resetting…" : "Reset password"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function fmtWhen(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }); }
  catch { return "—"; }
}

function ResetRequestsTab({ onChange }) {
  const [items, setItems] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [resetUser, setResetUser] = useState(null);

  const load = async () => {
    setLoading(true);
    const [a, b] = await Promise.all([api.get("/auth/reset-requests"), api.get("/users")]);
    setItems(a.data); setUsers(b.data); setLoading(false);
    onChange?.();
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const dismiss = async (req) => {
    await api.post(`/auth/reset-requests/${req.id}/resolve?status=dismissed`);
    load();
  };

  const startReset = (req) => {
    const u = users.find((x) => x.id === req.user_id || x.email === req.email);
    if (!u) { toast.error("No matching account for this email"); return; }
    setResetUser(u);
  };

  if (loading) return <LoadingState />;

  const pending = items.filter((x) => x.status === "pending");
  const resolved = items.filter((x) => x.status !== "pending");

  return (
    <div data-testid="admin-reset-requests-tab">
      <div className="flex items-center justify-between mb-3">
        <p className="text-2 text-sm">{pending.length} pending request{pending.length === 1 ? "" : "s"}. Reset the user's password, then share the temporary password with them.</p>
        <Button variant="ghost" size="sm" onClick={load} className="text-2"><RefreshCw className="h-3.5 w-3.5" /> Refresh</Button>
      </div>

      {items.length === 0 ? (
        <GlassCard className="p-10 text-center">
          <Inbox className="h-7 w-7 text-3 mx-auto mb-2" />
          <div className="text-1 font-medium">No password reset requests</div>
          <div className="text-2 text-sm mt-1">When a user submits "Forgot password?" they'll show up here.</div>
        </GlassCard>
      ) : (
        <GlassCard className="p-0 overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-3 text-[11px] uppercase tracking-wider bg-white/[0.03]">
                <th className="text-left p-3">Email</th>
                <th className="text-left p-3">User</th>
                <th className="text-left p-3">Requested</th>
                <th className="text-left p-3">Status</th>
                <th className="text-right p-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {[...pending, ...resolved].map((req) => (
                <tr key={req.id} className="border-t border-white/5 hover:bg-white/[0.04]" data-testid={`admin-reset-request-${req.id}`}>
                  <td className="p-3 text-1 font-medium">{req.email}</td>
                  <td className="p-3 text-2">{req.user_exists ? `${req.full_name || "—"} · ${req.role || "—"}` : <span className="text-3 italic">No account found</span>}</td>
                  <td className="p-3 text-2">{fmtWhen(req.created_at)}</td>
                  <td className="p-3">
                    <StatusPill tone={req.status === "pending" ? "amber" : req.status === "resolved" ? "emerald" : "neutral"}>{req.status}</StatusPill>
                  </td>
                  <td className="p-3 text-right whitespace-nowrap">
                    {req.status === "pending" && (
                      <>
                        {req.user_exists && (
                          <Button onClick={() => startReset(req)} variant="ghost" size="sm" className="text-cyan-300 hover:text-cyan-200" data-testid={`admin-reset-request-action-${req.id}`}><KeyRound className="h-4 w-4" /> Reset password</Button>
                        )}
                        <Button onClick={() => dismiss(req)} variant="ghost" size="sm" className="text-2 hover:text-white" data-testid={`admin-reset-request-dismiss-${req.id}`}>Dismiss</Button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </GlassCard>
      )}

      {resetUser && (
        <ResetPasswordDialog
          user={resetUser}
          onClose={() => setResetUser(null)}
          onDone={() => { setResetUser(null); load(); }}
        />
      )}
    </div>
  );
}
